import { AppFormModel } from '../models/AppFormModel';
import { MglAdapter } from '../../../../../shared/models/adapter/MglAdapter';
import { KindApi } from '../../../../../shared/models/generated/MitgliedschaftKindApi';
import { MitgliedschaftAnlageErweiterungApi } from '../../../../../shared/models/generated/MitgliedschaftMitgliedschaftAnlageErweiterungApi';
import { MitgliedschaftAnlageErweiterungProfiWerberApi } from '../../../../../shared/models/generated/MitgliedschaftMitgliedschaftAnlageErweiterungProfiWerberApi';
import { SimulationTypeApi } from '../../../../../shared/models/generated/TypesSimulationTypeApi';
import { User } from '../../../../../shared/models/api/User/User';
import { PersonalDataModel } from '../../../../../shared/models/view/PersonalData';
import { AdresseModel } from '../../../../../shared/models/view/Adresse';
import { MitgliedschaftGutscheinKooperationApi } from '../../../../../shared/models/generated/MitgliedschaftMitgliedschaftGutscheinKooperationApi';
import { ProfiwerberViewModelApi } from '../../../../../shared/models/generated/MitgliedschaftProfiwerberViewModelApi';
import { AppApplicationModel } from '../models/AppApplicationModel';

/**
 * Converts insurance view models to api models and vice versa.
 */
export class AppModelAdapter extends MglAdapter {

	/**
	 * Converts a view model to an api model.
	 */
	public static getAnlageApi(formModel: AppFormModel,
		user: User,
		partnerAdvertisingData: { [key: string]: any },
		options: MglAddMembersAppOptions,
		onlySimulate: boolean): MitgliedschaftAnlageErweiterungApi | MitgliedschaftAnlageErweiterungProfiWerberApi {

		let requestModel: MitgliedschaftAnlageErweiterungApi | MitgliedschaftAnlageErweiterungProfiWerberApi;

		if (options.app.issalesman) {
			requestModel = new MitgliedschaftAnlageErweiterungProfiWerberApi();
		} else {
			requestModel = new MitgliedschaftAnlageErweiterungApi();
		}
		requestModel.PlusUpgrade = options.app.isplusupgrade;
		requestModel.PremiumUpgrade = options.app.ispremiumupgrade;
		requestModel.Werbeschluessel = formModel.Werbeschluessel ? formModel.Werbeschluessel : (options.app.advertisingkey ? Number(options.app.advertisingkey) : undefined);

		if (onlySimulate) {
			requestModel.BestimmungenAkzepiert = true;
			requestModel.WiderrufAkzeptiert = true;
		} else {
			requestModel.WiderrufAkzeptiert = formModel.WiederrufAkzeptiert;
			requestModel.BestimmungenAkzepiert = formModel.BestimmungenAkzeptiert;
		}

		requestModel.HatKinder = formModel.Kinder && formModel.Kinder.length > 0;
		requestModel.Kinder = new Array<KindApi>();

		if (formModel.Partner) {
			requestModel.HatPartner = true;
			requestModel.Partner = this.getPartnerApi(formModel.Partner);
		}

		if (requestModel.HatKinder) {
			for (const child of formModel.Kinder) {
				requestModel.Kinder.push(this.getKindApi(child));
			}
		}

		requestModel.Agenturnummer = Number(options.app.agencynumber);
		if (options.app.ispartneradvertisement) {
			requestModel.Agenturnummer = undefined;
			requestModel.Werbernummer = Number(user.MglNo);
			requestModel.Werbegeschenk = partnerAdvertisingData.productActiveOrderNumber;
		}

		if (formModel.Bonus.Bonuscode) {
			requestModel.Gutscheindaten = new MitgliedschaftGutscheinKooperationApi({
				Code: formModel.Bonus.Bonuscode,
				EmailKooperation: formModel.Bonus.EmailBonus,
				Gutscheinwert: formModel.Bonus.TextBonus
			});
		}

		requestModel.SimulationType = onlySimulate ? SimulationTypeApi.OnlyValidate : SimulationTypeApi.None;

		if (options.app.issalesman) {
			(requestModel as MitgliedschaftAnlageErweiterungProfiWerberApi).Profiwerbernummer = (formModel.Profiwerbernummer && formModel.Profiwerbernummer.length > 0) ? formModel.Profiwerbernummer : options.app.salesmannumberdefaultvalue;
			(requestModel as MitgliedschaftAnlageErweiterungProfiWerberApi).Pruefziffer = formModel.Pruefziffer;
		}

		return requestModel;
	}

	static getAntragsstellerModel(user: User): PersonalDataModel {
		const userData = user.UserData;
		const model = new PersonalDataModel();
		model.Mitgliedsnummer = user.MglNo;
		model.AnredeKennzeichen = userData.Anrede;
		model.Titel = userData.Titel;
		model.Vorname = userData.Vorname;
		model.Nachname = userData.Nachname;
		model.Adresse = new AdresseModel();
		model.Adresse.StrasseHausnummer = userData.Anschrift.StrasseHausnummer;
		model.Adresse.Postfachnummer = userData.Anschrift.Postfachnummer;
		model.Adresse.Postleitzahl = userData.Anschrift.Postleitzahl;
		model.Adresse.Ort = userData.Anschrift.Ort;
		model.Geburtsdatum = userData.Geburtsdatum;

		return model;
	}

	public static getIdentifyingPersonalData(user: User): { [key: string]: any } {
		return {
			vorname: user.UserData.Vorname,
			nachname: user.UserData.Nachname,
			strasse: user.UserData.Anschrift.StrasseHausnummer,
			ort: user.UserData.Anschrift.Ort,
			plz: user.UserData.Anschrift.Postleitzahl
		};
	}

	public static getProfiwerberApi(formModel: AppFormModel, werbeschluessel: string): ProfiwerberViewModelApi {
		const result = new ProfiwerberViewModelApi();
		result.Profiwerbernummer = formModel.Profiwerbernummer;
		result.Pruefziffer = formModel.Pruefziffer;
		result.Werbeschluessel = Number(werbeschluessel);
		return result;
	}

	public static updateEcommerceModel(model: EcommerceBasisPaket, appModel: AppApplicationModel, options: MglAddMembersAppOptions): EcommerceBasisPaket {
		const variant = [];
		if (appModel.FormModel.Kinder && appModel.FormModel.Kinder.length > 0) {
			let hasChild = false;
			let hasJuv = false;
			appModel.FormModel.Kinder.forEach((kind) => {
				if (kind.IsJuv) {
					hasJuv = true;
				} else {
					hasChild = true;
				}
			});
			if (hasChild) {
				variant.push("Kind");
			}
			if (hasJuv) {
				variant.push("Juv");
			}
		}

		if (appModel.FormModel.Partner) {
			variant.push("Partner");
		}

		model.productName = options.app.ecommercemodel.productname;
		model.productID = options.app.ecommercemodel.productname;
		model.productPrice = appModel.ContractInfoModel.Beitrag.toString();
		model.productQty = "1";
		model.productVariant = variant.join(" | ");
		model.productBrand = options.app.ecommercemodel.productbrand;
		model.checkOutPaymentMethod = options.app.ecommercemodel.payment;
		model.contractType = options.app.ecommercemodel.contracttype;
		model.orderTotalPrice = appModel.ContractInfoModel.Beitrag.toString();
		model.productType = options.app.ecommercemodel.producttype;
		model.productCategory = options.app.ecommercemodel.productcategory;

		if (model.orderID === undefined || model.orderID.length === 0) {
			model.orderID = options.app.ecommercemodel.orderid;
		}

		return model;
	}

}
